import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import api from '../../utils/api';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // Custom Link component that scrolls to top
  const ScrollToTopLink: React.FC<{ to: string; className?: string; children: React.ReactNode }> = ({ to, className, children }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      navigate(to);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <a href={to} onClick={handleClick} className={className}>
        {children}
      </a>
    );
  };

  const footerLinks = {
    company: [
      { name: t('nav.about'), href: '/about' },
      { name: t('nav.contact'), href: '/contact' },
      { name: t('nav.products'), href: '/products' },
      { name: t('nav.news'), href: '/news' },
    ],
    legal: [
      { name: t('footer.privacyPolicy', 'Privacy Policy'), href: '/privacy' },
      { name: t('footer.termsOfService', 'Terms of Service'), href: '/terms' },
    ],
    // products links are loaded from backend categories below
    products: [] as { name: string; href: string }[],
  };

  // Load categories for footer products links
  const [productLinks, setProductLinks] = useState<{ name: string; href: string }[]>([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get('/categories');
      if (Array.isArray(data) && data.length > 0) {
        const links = data
          .filter((c: any) => c.isActive)
          .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
          .map((c: any) => ({
            name: isRTL ? (c.nameAr || c.nameEn || c.name) : (c.nameEn || c.nameAr || c.name),
            href: `/category/${c.slug}`,
          }));
        setProductLinks(links);
      } else {
        setProductLinks([]);
      }
    } catch {
      setProductLinks([]);
    }
  }, [isRTL]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const contactInfo = [
    {
      icon: MapPinIcon,
      label: 'Address',
      value: '123 Green Energy Street, Tech City, TC 12345',
    },
    {
      icon: PhoneIcon,
      label: 'Phone',
      value: '+1 (555) 123-4567',
    },
    {
      icon: EnvelopeIcon,
      label: 'Email',
      value: 'info@rfbox.com',
    },
    {
      icon: ClockIcon,
      label: 'Business Hours',
      value: 'Mon-Fri: 9:00 AM - 6:00 PM',
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-8 sm:py-10 lg:py-12 border-b border-gray-700/30">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-sm">RF</span>
                </div>
                <div className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                  <span className="text-lg font-bold">RFBOX</span>
                  <div className="text-xs text-gray-400">{t('footer.companyLabel', 'company')}</div>
                </div>
              </div>
              <p className="text-gray-300 mb-4 max-w-md leading-relaxed text-sm">
                {t('footer.description', 'Leading the green energy revolution with advanced lithium battery solutions. Your trusted partner for sustainable energy technology and exceptional service.')}
              </p>
              <div className="text-xs text-primary-400 font-semibold">
                {t('footer.tagline', 'The Future Possibility Powers')}
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-base font-bold mb-4 text-white">{t('footer.company', 'Company')}</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <ScrollToTopLink
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group text-sm"
                    >
                      <span className={`w-1 h-1 bg-primary-500 rounded-full ${isRTL ? 'ml-2' : 'mr-2'} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}></span>
                      {link.name}
                    </ScrollToTopLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products Links (loaded from backend categories) */}
            <div>
              <h3 className="text-base font-bold mb-4 text-white">{t('footer.products', 'Products')}</h3>
              <ul className="space-y-2">
                {productLinks.map((link) => (
                  <li key={link.name}>
                    <ScrollToTopLink
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group text-sm"
                    >
                      <span className={`w-1 h-1 bg-primary-500 rounded-full ${isRTL ? 'ml-2' : 'mr-2'} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}></span>
                      {link.name}
                    </ScrollToTopLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-base font-bold mb-4 text-white">{t('footer.legal', 'Legal')}</h3>
              <ul className="space-y-2">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <ScrollToTopLink
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200 flex items-center group text-sm"
                    >
                      <span className={`w-1 h-1 bg-primary-500 rounded-full ${isRTL ? 'ml-2' : 'mr-2'} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}></span>
                      {link.name}
                    </ScrollToTopLink>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700/50 py-6 sm:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
            {/* Copyright */}
            <div className="text-center md:text-left">
              <p className="text-gray-400 text-xs sm:text-sm">
                {t('footer.copyright', '© {{year}} RFBOX company. All rights reserved.', { year: currentYear })}
              </p>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              {/* Facebook */}
              <button
                className="relative p-2.5 sm:p-3 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-primary-600 rounded-lg transition-all duration-300 hover:scale-110 transform group"
                onClick={() => window.open('https://www.facebook.com/RFboxenergy/', '_blank')}
                aria-label={t('footer.facebook', 'Facebook')}
              >
                <span className="sr-only">{t('footer.facebook', 'Facebook')}</span>
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22 12a10 10 0 10-11.5 9.9v-7h-2v-3h2v-2.3c0-2 1.2-3.1 3-3.1.9 0 1.8.16 1.8.16v2h-1c-1 0-1.3.64-1.3 1.3V12h2.2l-.35 3h-1.85v7A10 10 0 0022 12z" />
                </svg>
              </button>

              {/* X (Twitter) */}
              <button
                className="relative p-2.5 sm:p-3 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700 rounded-lg transition-all duration-300 hover:scale-110 transform group"
                onClick={() => window.open('https://x.com/Rfboxenergy', '_blank')}
                aria-label={t('footer.x', 'X')}
              >
                <span className="sr-only">{t('footer.x', 'X')}</span>
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18 2h3l-8 9 8 11h-5l-5-7-5 7H1l8-10L1 2h5l5 6 5-6z"/>
                </svg>
              </button>

              {/* LinkedIn */}
              <button
                className="relative p-2.5 sm:p-3 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-blue-600 rounded-lg transition-all duration-300 hover:scale-110 transform group"
                onClick={() => window.open('https://www.linkedin.com/company/rfbox/', '_blank')}
                aria-label={t('footer.linkedin', 'LinkedIn')}
              >
                <span className="sr-only">{t('footer.linkedin', 'LinkedIn')}</span>
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </button>

              {/* Instagram */}
              <button
                className="relative p-2.5 sm:p-3 text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gradient-to-r hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 rounded-lg transition-all duration-300 hover:scale-110 transform group"
                onClick={() => window.open('https://www.instagram.com/rfbox19/', '_blank')}
                aria-label={t('footer.instagram', 'Instagram')}
              >
                <span className="sr-only">{t('footer.instagram', 'Instagram')}</span>
                <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3a5 5 0 110 10 5 5 0 010-10zm6.5-.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
